import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Users, Building2, BookOpen, TrendingUp, GraduationCap, Brain, Rocket, BookMarked, School, Trophy, ArrowUpRight, ArrowDownRight, Activity, RefreshCw, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import Card from '../components/Common/Card';
import SystemLogs from '../components/Dashboard/SystemLogs';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { sanitizeInput } from '../utils/sanitize';

const StatCard = ({ title, value, icon: Icon, color, trend, delay = 0, subtitle }) => (
  <div
    className="group relative bg-white dark:bg-[#002147] rounded-xl p-5 shadow-md hover:shadow-xl dark:hover:shadow-[#001030]/60 transition-all duration-300 animate-slide-up overflow-hidden cursor-pointer border border-gray-100 dark:border-[#1a3884]/35"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="relative z-10">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{title}</p>
          {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${color} shadow-sm group-hover:scale-110 transition-all duration-300`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {value}
          </p>
        </div>
        {trend && (
          <div className="flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-semibold bg-[#1a3884]/12 dark:bg-[#1a3884]/20 text-[#1a3884] dark:text-[#1a3884]">
            <TrendingUp className="w-3 h-3" />
            <span>{trend}</span>
          </div>
        )}
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch dashboard data on mount (redirected roles never render this component)
  useEffect(() => {
    fetchDashboardData();

    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get('/analytics/dashboard');
      if (data.success) {
        setStats(data.data);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 404) {
        setError(true);
      } else if (error.response?.status === 403) {
        setError(true);
      } else if (error.response?.status === 500) {
        setError(true);
      } else if (!navigator.onLine) {
        setError(true);
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Role-based redirection - MUST be after all hooks
  if (user) {
    switch (user.role) {
      case 'admin':
        return <Navigate to="/admin/dashboard" replace />;
      case 'college_admin':
        return <Navigate to="/college/dashboard" replace />;
      case 'student':
        return <Navigate to="/student/dashboard" replace />;
      case 'recruiter':
        return <Navigate to="/recruiter/dashboard" replace />;
      case 'office_admin':
        return <Navigate to="/admin/dashboard" replace />;
      default:
        break;
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-emerald-200"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-emerald-600 border-t-transparent absolute inset-0"></div>
        </div>
        <p className="text-gray-600 font-medium animate-pulse flex items-center">
          <GraduationCap className="w-5 h-5 mr-2 text-emerald-600" />
          Loading dashboard...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <p className="text-gray-700 font-semibold">Failed to load dashboard data</p>
        <p className="text-gray-500 text-sm">Check your connection or try refreshing.</p>
        <button
          onClick={fetchDashboardData}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
        >
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Educational Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-float" style={{ animationDelay: '4s' }}></div>
      </div>

      {/* Welcome Section */}
      <div className="relative bg-gradient-to-br from-[#002147] via-[#002A5C] to-[#1a3884] rounded-xl shadow-lg p-6 text-white overflow-hidden animate-slide-down">
        {/* Subtle Pattern Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M50 10 L90 30 L90 70 L50 90 L10 70 L10 30 Z\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' opacity=\'0.3\'/%3E%3Ccircle cx=\'50\' cy=\'50\' r=\'5\' fill=\'white\' opacity=\'0.5\'/%3E%3C/svg%3E")' }}></div>
        </div>

        {/* Floating Icons - Smaller */}
        <div className="absolute top-5 right-10 opacity-10 animate-float">
          <GraduationCap className="w-16 h-16 text-white" />
        </div>
        <div className="absolute bottom-5 left-10 opacity-10 animate-float" style={{ animationDelay: '2s' }}>
          <Brain className="w-12 h-12 text-white" />
        </div>

        {/* Floating Orbs - Subtle */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-cyan-400/20 to-blue-400/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-br from-emerald-400/20 to-teal-400/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>

        <div className="relative z-10">
          <div className="flex items-start space-x-3 mb-3">
            <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl shadow-md border border-white/20 flex-shrink-0">
              <Rocket className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold mb-1 leading-tight">
                Welcome back, {sanitizeInput(user?.fullName)}!
              </h1>
              <p className="text-white/80 text-xs md:text-sm font-medium flex items-center">
                <Trophy className="w-4 h-4 mr-1.5 flex-shrink-0" />
                Manage institutions and monitor platform activity
              </p>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between flex-wrap gap-2 bg-white/10 backdrop-blur-sm rounded-lg p-2.5 border border-white/10">
            <div className="flex items-center space-x-2">
              <div className="relative flex-shrink-0">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <div className="absolute inset-0 w-2 h-2 bg-green-400 rounded-full animate-ping"></div>
              </div>
              <p className="text-white text-xs md:text-sm">
                <span className="font-medium">All systems operational • </span>
                <span className="font-semibold">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              {lastUpdated && (
                <span className="text-white/60 text-xs">
                  Stats as of {(() => {
                    const date = new Date(lastUpdated);
                    return !isNaN(date) ? date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : 'N/A';
                  })()}
                </span>
              )}
              <button
                onClick={fetchDashboardData}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-white text-xs font-medium transition-colors"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid - Educational Theme */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Total Institutions"
          subtitle="Learning Centers"
          value={stats?.colleges?.total ?? '—'}
          icon={School}
          color="bg-gradient-to-br from-[#00152E] to-[#1a3884]"
          delay={100}
        />
        <StatCard
          title="Active Learners"
          subtitle="Enrolled Students"
          value={stats?.students?.active ?? '—'}
          icon={GraduationCap}
          color="bg-gradient-to-br from-[#00152E] to-[#1a3884]"
          delay={200}
        />
        <StatCard
          title="Active Courses"
          subtitle="Learning Programs"
          value={stats?.courses?.active ?? '—'}
          icon={BookMarked}
          color="bg-gradient-to-br from-[#00152E] to-[#1a3884]"
          delay={300}
        />
        <StatCard
          title="Mentoring Sessions"
          subtitle="Knowledge Shared"
          value={stats?.sessions?.completed ?? '—'}
          icon={Brain}
          color="bg-gradient-to-br from-[#00152E] to-[#1a3884]"
          delay={400}
        />
      </div>

      {/* Recent Activity & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="System Logs & Activity">
          <SystemLogs />
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-[#1a3884]/30">
            <button
              onClick={() => navigate('/admin/activity-log')}
              className="text-[#1a3884] hover:text-[#00152E] text-xs font-semibold flex items-center gap-1 transition-colors"
            >
              View All Activity <span aria-hidden>→</span>
            </button>
          </div>
        </Card>

        <Card title="Quick Actions">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/colleges/onboard')}
              className="group relative p-4 border border-[#1a3884]/30 dark:border-[#1a3884]/50 rounded-lg hover:border-[#1a3884] bg-[#1a3884]/5 dark:bg-[#001f40] hover:bg-[#1a3884]/10 dark:hover:bg-[#1a3884]/20 hover:shadow-md transition-all duration-300 overflow-hidden"
            >
              <School className="w-7 h-7 text-[#1a3884] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <p className="text-xs font-semibold text-gray-900 dark:text-white">Add Institution</p>
            </button>
            <button
              onClick={() => navigate('/colleges')}
              className="group relative p-4 border border-[#0f766e]/30 dark:border-[#0f766e]/50 rounded-lg hover:border-[#2dd4bf] bg-[#0f766e]/5 dark:bg-[#001f40] hover:bg-[#0f766e]/10 dark:hover:bg-[#0f766e]/20 hover:shadow-md transition-all duration-300 overflow-hidden"
            >
              <Building2 className="w-7 h-7 text-[#2dd4bf] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <p className="text-xs font-semibold text-gray-900 dark:text-white">Manage Institutes</p>
            </button>
            <button
              onClick={() => navigate('/users')}
              className="group relative p-4 border border-[#1e40af]/30 dark:border-[#1e40af]/50 rounded-lg hover:border-[#1a3884] bg-[#1e40af]/5 dark:bg-[#001f40] hover:bg-[#1e40af]/10 dark:hover:bg-[#1e40af]/20 hover:shadow-md transition-all duration-300 overflow-hidden"
            >
              <Users className="w-7 h-7 text-[#1a3884] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <p className="text-xs font-semibold text-gray-900 dark:text-white">Manage Users</p>
            </button>
            <button
              onClick={() => navigate('/analytics')}
              className="group relative p-4 border border-[#b45309]/30 dark:border-[#b45309]/50 rounded-lg hover:border-[#f59e0b] bg-[#b45309]/5 dark:bg-[#001f40] hover:bg-[#b45309]/10 dark:hover:bg-[#b45309]/20 hover:shadow-md transition-all duration-300 overflow-hidden"
            >
              <Activity className="w-7 h-7 text-[#f59e0b] mx-auto mb-2 group-hover:scale-110 transition-transform" />
              <p className="text-xs font-semibold text-gray-900 dark:text-white">View Analytics</p>
            </button>
          </div>
        </Card>
      </div>

      <Card title="📋 Platform Overview" className="animate-slide-up">
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-xs font-bold text-green-800 dark:text-green-400 uppercase tracking-wide">Active Institutes</span>
              </div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats?.colleges?.active ?? '—'}</p>
              <p className="text-xs text-green-600 dark:text-green-500 mt-1">Currently onboarded &amp; live</p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wide">Pending Approvals</span>
              </div>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{stats?.colleges?.pending ?? '—'}</p>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">Awaiting admin review</p>
            </div>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-600 text-right pt-1">
            Data reflects current database state. Use Refresh above for latest figures.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default Dashboard;
